//@flow
import React from 'react';
import {makeStyles} from '@material-ui/core';
import YouTubePlayer from 'react-youtube';

import {
  BANNER_MEDIA_ASPECT_RATIO,
  BANNER_LAYOUTS,
} from '../../../../../constants';
import {useFormContext} from 'react-hook-form';
import {
  MAIN_BANNER_FIELDS,
  MAIN_BANNER_DESKTOP_ID,
  MAIN_BANNER_MOBILE_ID,
  MAIN_BANNER_DESKTOP_EDITOR_ID,
  MAIN_BANNER_MOBILE_EDITOR_ID,
} from '../constants';

import {getYoutubePosterUrl} from '../../../../../../../utils/youtube';

import type {
  BannerContentPosition,
  BannerShapeType,
} from '../../../../../../../entities';

type StyleProps = {
  backgroundColor?: string,
  bannerSize: BannerShapeType,
};

const useClasses = ({backgroundColor, bannerSize}: StyleProps) =>
  makeStyles(theme => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: '100%',
      overflow: 'auto',
      background: '#F2F2F2',
      borderRadius: '.75rem',
      padding: '1rem',
    },
    editorSection: {
      display: 'flex',
      gap: '.5rem',
      flexDirection: 'column',
    },
    container: {
      display: 'flex',
      gap: '1rem',
      backgroundColor: backgroundColor || '#fff',
      aspectRatio: BANNER_MEDIA_ASPECT_RATIO[bannerSize].desktop,
      position: 'relative',
      overflow: 'hidden',
      minWidth: '720px',
      width: '100%',
      maxWidth: '800px',
    },
    image: {
      position: 'absolute',
      zIndex: 1,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    content: {
      width: '40%',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'center',
      position: 'absolute',
      zIndex: 2,
      gap: '.5rem',
      pointerEvents: 'none',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      margin: 0,
      color: 'inherit',
      lineHeight: '1.1',
    },
    subTitle: {
      fontSize: '.85rem',
      fontWeight: '500',
      margin: 0,
      color: 'inherit',
    },
    button: {
      margin: '0',
      marginTop: '1rem',
      padding: '.75rem 2rem',
      backgroundColor: '#000',
      color: '#fff',
      border: 'none',
      borderRadius: '80px',
      fontSize: '.85rem',
      fontWeight: '600',
      width: 'max-content',
      cursor: 'pointer',
    },
    mobileContainer: {
      display: 'flex',
      gap: '.25rem',
      backgroundColor: backgroundColor || '#fff',
      width: '425px',
      aspectRatio: BANNER_MEDIA_ASPECT_RATIO[bannerSize].mobile,
      position: 'relative',
      overflow: 'hidden',
    },
    videoContainer: {
      height: '100%',
      width: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      pointerEvents: 'none',
    },
    videoWrapDesktop: {
      height: '100%',
    },

    videoWrapMobile: {
      width: '100%',
    },

    videoIframe: {
      width: '100%',
      height: '100%',
    },

    posterVideoBlur: {
      filter: 'blur(24px)',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  }));

type BannerStyle = {
  content?: any,
  contentMobile?: any,
};
const getBannerStyles = (layout: BannerContentPosition): BannerStyle => {
  if (layout === BANNER_LAYOUTS.CENTER) {
    return {
      content: {
        left: '50%',
        right: 'initial',
        transform: 'translateX(-50%)',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
      },
      contentMobile: {
        width: '60%',
      },
    };
  }

  if (layout === BANNER_LAYOUTS.LEFT) {
    return {
      content: {
        right: '10%',
        left: 'initial',
      },
    };
  }

  if (layout === BANNER_LAYOUTS.RIGHT) {
    return {
      content: {
        left: '10%',
        right: 'initial',
      },
    };
  }

  return {};
};

export default function BannerEditor() {
  const {watch} = useFormContext();

  const layout = watch(MAIN_BANNER_FIELDS.CONTENT_POSITION);
  const buttonBgColor = watch(MAIN_BANNER_FIELDS.BUTTON_BACKGROUND_COLOR);
  const buttonTextColor = watch(MAIN_BANNER_FIELDS.BUTTON_TEXT_COLOR);
  const buttonText = watch(MAIN_BANNER_FIELDS.BUTTON_TEXT);
  const bannerTextColor = watch(MAIN_BANNER_FIELDS.TEXT_COLOR);
  const bannerBgColor = watch(MAIN_BANNER_FIELDS.BACKGROUND_COLOR);
  const bannerTitle = watch(MAIN_BANNER_FIELDS.TITLE);
  const bannerSubTitle = watch(MAIN_BANNER_FIELDS.SUBTITLE);

  const desktopImage = watch(MAIN_BANNER_FIELDS.IMAGE_DESKTOP);
  const mobileImage = watch(MAIN_BANNER_FIELDS.IMAGE_MOBILE);
  const videoUrl = watch(MAIN_BANNER_FIELDS.VIDEO_URL);
  const videoId = watch(MAIN_BANNER_FIELDS.VIDEO_ID);
  const bannerSize = watch(MAIN_BANNER_FIELDS.BANNER_SIZE);

  const classes = useClasses({
    backgroundColor: bannerBgColor,
    bannerSize,
  })();

  const bannerLayoutStyles = getBannerStyles(layout);

  const contentStyles = {
    ...bannerLayoutStyles.content,
    color: bannerTextColor,
  };

  const contentStylesMobile = {
    ...contentStyles,
    ...bannerLayoutStyles.contentMobile,
  };

  const buttonStyles = {
    backgroundColor: buttonBgColor,
    color: buttonTextColor,
  };

  if (videoUrl)
    return (
      <div className={classes.root}>
        <section className={classes.editorSection}>
          <strong>
            Versión Desktop {'(Doble Clic en la  imágen para moverla)'}
          </strong>

          <div className={classes.container}>
            <img
              src={getYoutubePosterUrl(videoId)}
              className={classes.posterVideoBlur}
              alt="poster blur"
            />
            <div className={classes.videoContainer}>
              <YouTubePlayer
                videoId={videoId}
                style={{
                  aspectRatio: '16/9',
                }}
                className={classes.videoWrapDesktop}
                iframeClassName={classes.videoIframe}
                opts={{
                  playerVars: {
                    autoplay: 1,
                    mute: 1,
                    loop: 1,
                    widget_referrer: 'https://www.google.com/',
                    controls: 0,
                  },
                }}
              />
            </div>
            <div
              className={classes.content}
              style={contentStyles}
              id={MAIN_BANNER_DESKTOP_ID}
            >
              <h3 className={classes.title} id="main_banner_home-title">
                {bannerTitle}
              </h3>
              <h4 className={classes.subTitle} id="main_banner_home-subtitle">
                {bannerSubTitle}
              </h4>
              {buttonText && (
                <button
                  className={classes.button}
                  style={buttonStyles}
                  id="main_banner_home-button"
                >
                  {buttonText}
                </button>
              )}
            </div>
          </div>
        </section>
        <section className={classes.editorSection}>
          <strong>
            Versión Mobile {'(Doble Clic en la  imágen para moverla)'}
          </strong>

          <div className={classes.mobileContainer}>
            <img
              src={getYoutubePosterUrl(videoId)}
              className={classes.posterVideoBlur}
              alt="poster blur"
            />
            <div className={classes.videoContainer}>
              <YouTubePlayer
                videoId={videoId}
                style={{
                  aspectRatio: '16/9',
                }}
                className={classes.videoWrapMobile}
                iframeClassName={classes.videoIframe}
                opts={{
                  playerVars: {
                    autoplay: 1,
                    mute: 1,
                    loop: 1,
                    widget_referrer: 'https://www.google.com/',
                    controls: 0,
                  },
                }}
              />
            </div>
            <div
              className={classes.content}
              style={contentStylesMobile}
              id={MAIN_BANNER_MOBILE_ID}
            >
              <h3 className={classes.title} id="main_banner_home-mobile-title">
                {bannerTitle}
              </h3>
              <h4
                className={classes.subTitle}
                id="main_banner_home-mobile-subtitle"
              >
                {bannerSubTitle}
              </h4>

              {buttonText && (
                <button
                  className={classes.button}
                  style={buttonStyles}
                  id="main_banner_home-mobile-button"
                >
                  {buttonText}
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    );

  if (!desktopImage || !mobileImage) return null;

  return (
    <div className={classes.root}>
      <section className={classes.editorSection}>
        <strong>Versión Desktop</strong>

        <div id={MAIN_BANNER_DESKTOP_EDITOR_ID} className={classes.container}>
          <img
            className={classes.image}
            src={URL.createObjectURL(desktopImage)}
            alt="desktop banner"
          />
          <div
            className={classes.content}
            style={contentStyles}
            id={MAIN_BANNER_DESKTOP_ID}
          >
            <h3 className={classes.title} id="main_banner_home-title">
              {bannerTitle}
            </h3>
            <h4 className={classes.subTitle} id="main_banner_home-subtitle">
              {bannerSubTitle}
            </h4>
            {buttonText && (
              <button
                className={classes.button}
                style={buttonStyles}
                id="main_banner_home-button"
              >
                {buttonText}
              </button>
            )}
          </div>
        </div>
      </section>
      <section className={classes.editorSection}>
        <strong>Versión Mobile</strong>

        <div
          src={URL.createObjectURL(mobileImage)}
          className={classes.mobileContainer}
          id={MAIN_BANNER_MOBILE_EDITOR_ID}
        >
          <img
            className={classes.image}
            src={URL.createObjectURL(mobileImage)}
            alt="mobile banner"
          />
          <div
            className={classes.content}
            style={contentStylesMobile}
            id={MAIN_BANNER_MOBILE_ID}
          >
            <h3 className={classes.title} id="main_banner_home-mobile-title">
              {bannerTitle}
            </h3>
            <h4
              className={classes.subTitle}
              id="main_banner_home-mobile-subtitle"
            >
              {bannerSubTitle}
            </h4>

            {buttonText && (
              <button
                className={classes.button}
                style={buttonStyles}
                id="main_banner_home-mobile-button"
              >
                {buttonText}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
